/* * Copyright (C) 2025 Hendrik. All Rights Reserved. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import './ExportMarks.css';
import {
    buildSectionMarkLookup,
    calculateSubjectResult,
    getFoundationGroupTarget,
    getFoundationSubjectDefinition
} from './foundationPhase';

const TERMS = [1, 2, 3, 4];

const Icons = {
    Download: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2-2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>,
    Refresh: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>,
    Check: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Class: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16"></path><path d="M4 12h16"></path><path d="M4 18h10"></path></svg>,
    Section: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"></rect><path d="M8 9h8"></path><path d="M8 13h8"></path></svg>
};

const thinBorder = {
    top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
    bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
    left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
    right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
};

const fillGrey = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

const safeNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const normalizePositiveNumber = (value, fallback) => {
    const parsed = safeNumber(value);
    return parsed && parsed > 0 ? parsed : fallback;
};

const normalizeGroupText = (value) => String(value || '').trim();

const getAssessmentGroupKey = (section) => {
    const assessmentGroup = normalizeGroupText(section?.assessment_group);
    if (assessmentGroup) {
        return `group:${assessmentGroup.toLowerCase()}`;
    }
    const category = normalizeGroupText(section?.category);
    if (category) {
        return `category:${category.toLowerCase()}`;
    }
    return `section:${section?.id}`;
};

const getAssessmentGroupLabel = (section) => {
    const assessmentGroup = normalizeGroupText(section?.assessment_group);
    if (assessmentGroup) {
        return assessmentGroup;
    }
    const category = normalizeGroupText(section?.category);
    if (category) {
        return category;
    }
    return section?.section_name || 'Assessment';
};

const buildAssessmentGroups = (sections) => {
    const groups = new Map();
    (sections || []).forEach((section) => {
        const key = getAssessmentGroupKey(section);
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                label: getAssessmentGroupLabel(section),
                sections: [],
                rawMax: 0
            });
        }
        const group = groups.get(key);
        group.sections.push(section);
        group.rawMax += safeNumber(section.max_mark) || 0;
    });
    return Array.from(groups.values()).sort((a, b) => {
        const labelA = String(a.label || '').toLowerCase();
        const labelB = String(b.label || '').toLowerCase();
        if (labelA !== labelB) return labelA.localeCompare(labelB);
        return String(a.key).localeCompare(String(b.key));
    });
};

const getFoundationDefinitionForSubject = (subject) => (
    getFoundationSubjectDefinition(subject?.name, subject?.grade, subject?.grading_mode, subject?.foundation_template_code)
);

const buildDefaultAssessmentWeights = (subject) => {
    const foundationDefinition = getFoundationDefinitionForSubject(subject);
    return buildAssessmentGroups(subject.sections || []).reduce((acc, group) => {
        acc[group.key] = foundationDefinition
            ? getFoundationGroupTarget(foundationDefinition, group.label, group.rawMax > 0 ? group.rawMax : 100)
            : (group.rawMax > 0 ? group.rawMax : 100);
        return acc;
    }, {});
};

const calculateLevel = (percentage) => {
    const rounded = Math.round(percentage);
    if (rounded >= 80) return 7;
    if (rounded >= 70) return 6;
    if (rounded >= 60) return 5;
    if (rounded >= 50) return 4;
    if (rounded >= 40) return 3;
    if (rounded >= 30) return 2;
    return 1;
};

const getPassMark = (subjectName) => {
    const lower = (subjectName || '').toLowerCase();
    return lower.includes('home language') || lower.includes('huistaal') ? 50 : 40;
};

const getSelectedSections = (subject, selectedSectionsBySubject) => {
    const selected = selectedSectionsBySubject[subject.subject_id] || [];
    const selectedSet = new Set(selected.map(String));
    return (subject.sections || []).filter((section) => selectedSet.has(String(section.id)));
};

const buildSectionStats = (subject, section) => {
    const passMark = getPassMark(subject.name);
    const maxMark = safeNumber(section.max_mark) || 100;
    const validMarks = (subject.marks || [])
        .filter((mark) => String(mark.section_id) === String(section.id))
        .map((mark) => ({
            mark: safeNumber(mark.mark),
            studentId: String(mark.student_id)
        }))
        .filter((mark) => mark.mark !== null);

    const uniqueStudents = new Set(validMarks.map((mark) => mark.studentId));
    const totalWritten = validMarks.length;

    if (totalWritten === 0) {
        return {
            totalInClass: subject.student_count || 0,
            totalWritten: 0,
            classAverage: null,
            highest: '',
            lowest: '',
            failedCount: 0,
            passedCount: 0,
            levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
            totalLevels: '0/0'
        };
    }

    const percentages = validMarks.map((entry) => (entry.mark / maxMark) * 100);
    const levelDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
    let failedCount = 0;
    let passedCount = 0;

    percentages.forEach((percentage) => {
        const level = calculateLevel(percentage);
        levelDist[level] += 1;
        if (Math.round(percentage) >= passMark) passedCount += 1;
        else failedCount += 1;
    });

    const marksOnly = validMarks.map((entry) => entry.mark);
    const classAverage = percentages.reduce((sum, percentage) => sum + percentage, 0) / percentages.length;

    return {
        totalInClass: subject.student_count || uniqueStudents.size || 0,
        totalWritten,
        classAverage,
        highest: Math.max(...marksOnly),
        lowest: Math.min(...marksOnly),
        failedCount,
        passedCount,
        levelDist,
        totalLevels: `${Object.values(levelDist).reduce((sum, value) => sum + value, 0)}/${totalWritten}`
    };
};

const buildOverallStats = (subject, selectedSections) => {
    const passMark = getPassMark(subject.name);
    const markLookup = buildSectionMarkLookup(subject.marks || []);
    const totals = (subject.students || [])
        .map((student) => calculateSubjectResult({
            subjectName: subject.name,
            subjectGrade: subject.grade,
            gradingMode: subject.grading_mode,
            foundationTemplateCode: subject.foundation_template_code,
            sections: selectedSections,
            markLookup,
            studentId: parseInt(student.student_id, 10)
        }))
        .filter((entry) => entry.totalPossible > 0 && entry.percentage !== null);

    if (totals.length === 0) {
        return {
            totalWritten: 0,
            classAverage: null,
            highest: '',
            lowest: '',
            failedCount: 0,
            passedCount: 0,
            levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
            totalLevels: '0/0'
        };
    }

    const percentages = totals.map((entry) => entry.percentage);
    const levelDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
    let failedCount = 0;
    let passedCount = 0;

    percentages.forEach((percentage) => {
        const level = calculateLevel(percentage);
        levelDist[level] += 1;
        if (Math.round(percentage) >= passMark) passedCount += 1;
        else failedCount += 1;
    });

    const rawTotals = totals.map((entry) => entry.totalAchieved);
    const totalWritten = totals.length;

    return {
        totalWritten,
        classAverage: percentages.reduce((sum, percentage) => sum + percentage, 0) / percentages.length,
        highest: Math.max(...rawTotals),
        lowest: Math.min(...rawTotals),
        failedCount,
        passedCount,
        levelDist,
        totalLevels: `${Object.values(levelDist).reduce((sum, value) => sum + value, 0)}/${totalWritten}`
    };
};

const sanitizeSheetName = (name) => {
    const cleaned = String(name || '')
        .replace(/[\\/*?:]/g, ' ')
        .replace(/\[/g, ' ')
        .replace(/\]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return cleaned.slice(0, 31) || 'Sheet';
};

const uniqueSheetName = (workbook, baseName) => {
    const safeBase = sanitizeSheetName(baseName);
    if (!workbook.getWorksheet(safeBase)) {
        return safeBase;
    }
    let suffix = 2;
    while (suffix < 1000) {
        const suffixText = ` (${suffix})`;
        const trimmedBase = safeBase.slice(0, 31 - suffixText.length);
        const candidate = sanitizeSheetName(`${trimmedBase}${suffixText}`);
        if (!workbook.getWorksheet(candidate)) {
            return candidate;
        }
        suffix += 1;
    }
    return sanitizeSheetName(`${safeBase} Copy`);
};

// --------------------------------------------------------------------------------
// RAW MARKS SHEET GENERATOR - PURELY LISTS STUDENTS AND MARKS (NO STATS AT ALL)
// --------------------------------------------------------------------------------
const buildRawMarksSheet = (workbook, subject, selectedSections, selectedTerm, username, assessmentWeightTargets) => {
    const sheetName = uniqueSheetName(workbook, `${subject.class_name || subject.name || 'Class'} Marks`);
    const worksheet = workbook.addWorksheet(sheetName, {
        views: [{ state: 'frozen', ySplit: 5 }]
    });

    const selectedSectionSet = new Set(selectedSections.map((section) => String(section.id)));
    const assessmentGroups = buildAssessmentGroups(selectedSections);
    const foundationDefinition = getFoundationDefinitionForSubject(subject);

    const groupSummaries = assessmentGroups.map((group) => {
        const defaultTarget = foundationDefinition
            ? getFoundationGroupTarget(foundationDefinition, group.label, group.rawMax > 0 ? group.rawMax : 100)
            : (group.rawMax > 0 ? group.rawMax : 100);
        const targetWeight = normalizePositiveNumber(assessmentWeightTargets?.[group.key], defaultTarget);
        return { ...group, targetWeight };
    });

    const totalTargetWeight = groupSummaries.reduce((sum, group) => sum + group.targetWeight, 0);

    const roster = (subject.students || []).slice().sort((a, b) => {
        const lastA = String(a.last_name || '').toLowerCase();
        const lastB = String(b.last_name || '').toLowerCase();
        if (lastA !== lastB) return lastA.localeCompare(lastB);
        return String(a.first_name || '').toLowerCase().localeCompare(String(b.first_name || '').toLowerCase());
    });

    const marksByStudent = new Map();
    (subject.marks || []).forEach((mark) => {
        if (!selectedSectionSet.has(String(mark.section_id))) return;
        const studentKey = String(mark.student_id);
        if (!marksByStudent.has(studentKey)) {
            marksByStudent.set(studentKey, {});
        }
        marksByStudent.get(studentKey)[String(mark.section_id)] = mark.mark;
    });

    const headers = [
        'No',
        'Surname',
        'Name',
        ...selectedSections.map((section) => `${section.section_name} (${section.max_mark || ''})`),
        ...groupSummaries.flatMap((group) => [
            `${group.label} raw`,
            `${group.label} weighted (out of ${group.targetWeight})`
        ]),
        'Raw total',
        `Weighted total (out of ${totalTargetWeight})`,
        'Weighted %'
    ];

    worksheet.addRow([`${subject.grade || ''} ${subject.class_name || subject.name || 'Class'} Marks`.trim()]);
    worksheet.addRow([`Term ${selectedTerm}`, `Subject ${subject.name || ''}`, `Generated by ${username}`]);
    worksheet.addRow([
        `Assessment groups scaled to ${totalTargetWeight} marks`,
        `Groups: ${groupSummaries.length}`,
        'Raw marks are still shown for comparison'
    ]);
    worksheet.addRow([]);
    worksheet.addRow(headers);

    worksheet.mergeCells(1, 1, 1, headers.length);
    worksheet.mergeCells(2, 1, 2, headers.length);
    worksheet.mergeCells(3, 1, 3, headers.length);

    const styleRow = (rowNumber, style) => {
        const row = worksheet.getRow(rowNumber);
        row.eachCell((cell) => {
            if (style.font) cell.font = { ...(cell.font || {}), ...style.font };
            if (style.fill) cell.fill = style.fill;
            if (style.border) cell.border = style.border;
            if (style.alignment) cell.alignment = { ...(cell.alignment || {}), ...style.alignment };
        });
    };

    styleRow(1, {
        font: { bold: true, size: 16 },
        fill: fillGrey('FFD9D9D9'),
        border: thinBorder,
        alignment: { horizontal: 'center', vertical: 'middle' }
    });

    styleRow(2, {
        font: { italic: true },
        fill: fillGrey('FFF5F5F5'),
        border: thinBorder,
        alignment: { horizontal: 'center', vertical: 'middle' }
    });

    styleRow(3, {
        font: { italic: true },
        fill: fillGrey('FFF9FBF9'),
        border: thinBorder,
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true }
    });

    styleRow(5, {
        font: { bold: true },
        fill: fillGrey('FFE5E5E5'),
        border: thinBorder,
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true }
    });

    roster.forEach((student, index) => {
        const row = [index + 1, student.last_name || '', student.first_name || ''];
        let totalMark = 0;
        let weightedTotal = 0;

        selectedSections.forEach((section) => {
            const studentMarks = marksByStudent.get(String(student.student_id)) || {};
            const markValue = safeNumber(studentMarks[String(section.id)]);
            row.push(markValue !== null ? markValue : '');
            if (markValue !== null) {
                totalMark += markValue;
            }
        });

        groupSummaries.forEach((group) => {
            const studentMarks = marksByStudent.get(String(student.student_id)) || {};
            let groupMark = 0;
            group.sections.forEach((section) => {
                const markValue = safeNumber(studentMarks[String(section.id)]);
                if (markValue !== null) {
                    groupMark += markValue;
                }
            });
            const groupWeighted = group.rawMax > 0 ? (groupMark / group.rawMax) * group.targetWeight : null;
            row.push(group.rawMax > 0 ? Number(groupMark).toFixed(1) : '');
            row.push(groupWeighted !== null ? Number(groupWeighted).toFixed(1) : '');

            if (groupWeighted !== null) {
                weightedTotal += groupWeighted;
            }
        });

        row.push(totalMark > 0 ? Number(totalMark).toFixed(1) : '');
        row.push(totalTargetWeight > 0 ? Number(weightedTotal).toFixed(1) : '');
        const finalPercent = totalTargetWeight > 0 ? Math.round((weightedTotal / totalTargetWeight) * 100) : null;
        row.push(finalPercent !== null ? finalPercent : '');

        const addedRow = worksheet.addRow(row);
        addedRow.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    worksheet.columns = [
        { width: 8 },
        { width: 18 },
        { width: 18 },
        ...selectedSections.map(() => ({ width: 14 })),
        ...groupSummaries.flatMap(() => [{ width: 12 }, { width: 18 }]),
        { width: 12 },
        { width: 18 },
        { width: 12 }
    ];
};
// --------------------------------------------------------------------------------


export default function ExportMarks() {
    const username = localStorage.getItem('username') || 'Teacher';
    const [selectedTerm, setSelectedTerm] = useState(1);
    const [includeMarksSheets, setIncludeMarksSheets] = useState(false);
    const [assessmentWeightsBySubject, setAssessmentWeightsBySubject] = useState({});
    const [subjects, setSubjects] = useState([]);
    const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
    const [selectedSectionsBySubject, setSelectedSectionsBySubject] = useState({});
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');

    const groupedByGrade = useMemo(() => {
        return subjects.reduce((acc, subject) => {
            const grade = subject.grade || 'Other';
            if (!acc[grade]) acc[grade] = [];
            acc[grade].push(subject);
            return acc;
        }, {});
    }, [subjects]);

    const sortedGrades = useMemo(() => {
        return Object.keys(groupedByGrade).sort((a, b) => {
            if (a === 'Other') return 1;
            if (b === 'Other') return -1;
            return parseInt(a, 10) - parseInt(b, 10);
        });
    }, [groupedByGrade]);

    const loadData = useCallback(async (term) => {
        setLoading(true);
        setError('');
        try {
            const dashboardRes = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_dashboard.php`, {
                params: { term }
            });
            const subjectRows = dashboardRes.data.subjects || [];

            const detailedSubjects = await Promise.all(subjectRows.map(async (subject) => {
                try {
                    const [sectionsRes, marksRes, classMarksRes] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                            params: { subject_id: subject.subject_id, term }
                        }),
                        axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php`, {
                            params: { subject_id: subject.subject_id, term }
                        }),
                        axios.get(`${process.env.REACT_APP_API_URL}/fetch_class_marks.php`, {
                            params: { subject_id: subject.subject_id, term }
                        })
                    ]);
                    return {
                        ...subject,
                        sections: sectionsRes.data.sections || [],
                        marks: marksRes.data.marks || [],
                        students: classMarksRes.data.students || []
                    };
                } catch (subjectError) {
                    return {
                        ...subject,
                        sections: [],
                        marks: [],
                        students: []
                    };
                }
            }));

            setSubjects(detailedSubjects);
           setSelectedSubjectIds([]);

            const defaultWeights = {};
            detailedSubjects.forEach((subject) => {
                defaultWeights[subject.subject_id] = buildDefaultAssessmentWeights(subject);
            });
            setAssessmentWeightsBySubject(defaultWeights);

           setSelectedSectionsBySubject({});
        } catch (fetchError) {
            setSubjects([]);
            setSelectedSubjectIds([]);
            setSelectedSectionsBySubject({});
            setAssessmentWeightsBySubject({});
            setError('Failed to load export data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData(selectedTerm);
    }, [loadData, selectedTerm]);

const toggleSubject = (subject) => {
        const subjectKey = String(subject.subject_id);
        const isCurrentlySelected = selectedSubjectIds.includes(subjectKey);

        if (isCurrentlySelected) {
            // UNCHECKING: Remove the class and clear its selected sections
            setSelectedSubjectIds((current) => current.filter((id) => id !== subjectKey));
            setSelectedSectionsBySubject((current) => {
                const next = { ...current };
                next[subjectKey] = []; 
                return next;
            });
        } else {
            // CHECKING: Add the class and auto-select all its sections
            setSelectedSubjectIds((current) => [...current, subjectKey]);
            setSelectedSectionsBySubject((current) => ({
                ...current,
                [subjectKey]: (subject.sections || []).map((section) => String(section.id))
            }));
        }
    };
    const toggleSection = (subjectId, sectionId) => {
        const subjectKey = String(subjectId);
        const sectionKey = String(sectionId);
        setSelectedSectionsBySubject((current) => {
            const next = { ...current };
            const selected = new Set((next[subjectKey] || []).map(String));
            if (selected.has(sectionKey)) {
                selected.delete(sectionKey);
            } else {
                selected.add(sectionKey);
            }
            next[subjectKey] = Array.from(selected);
            return next;
        });
    };

 const selectAllSections = (subject) => {
        const subjectKey = String(subject.subject_id);
        
        // Auto-tick the main class checkbox if it isn't already
        setSelectedSubjectIds((current) => 
            current.includes(subjectKey) ? current : [...current, subjectKey]
        );

        // Select all sections
        setSelectedSectionsBySubject((current) => ({
            ...current,
            [subjectKey]: (subject.sections || []).map((section) => String(section.id))
        }));
    };

    const updateAssessmentWeight = (subjectId, groupKey, value) => {
        const subjectKey = String(subjectId);
        setAssessmentWeightsBySubject((current) => ({
            ...current,
            [subjectKey]: {
                ...(current[subjectKey] || {}),
                [groupKey]: value
            }
        }));
    };

    const exportWorkbook = async () => {
        const selectedSubjects = subjects.filter((subject) => selectedSubjectIds.includes(String(subject.subject_id)));
        if (selectedSubjects.length === 0) {
            alert('Select at least one class to export.');
            return;
        }

        setExporting(true);
        try {
            const workbook = new ExcelJS.Workbook();
            workbook.creator = username;
            workbook.created = new Date();

            const worksheet = workbook.addWorksheet('Class Statistics', {
                views: [{ state: 'frozen', ySplit: 4 }]
            });

            const headers = [
                'Grade', 'Class', 'Subject', 'Assessment', 'Total in class', 'Total written', 'Class average',
                'Highest mark', 'Lowest mark', 'Total failed', '% Failures', 'Total passed', '% Passed',
                '0 - 29', '30 - 39', '40 - 49', '50 - 59', '60 - 69', '70 - 79', '80 - 100',
                'Total (1-7 = Total/Total written)'
            ];

            worksheet.addRow(['Marksure Class Statistics']);
            worksheet.addRow([`Term ${selectedTerm}`, `Generated by ${username}`, new Date().toLocaleString()]);
            worksheet.addRow([]);
            worksheet.addRow(headers);

            worksheet.mergeCells(1, 1, 1, headers.length);
            worksheet.mergeCells(2, 1, 2, headers.length);

            const styleRow = (rowNumber, style) => {
                const row = worksheet.getRow(rowNumber);
                row.eachCell((cell) => {
                    if (style.font) cell.font = { ...(cell.font || {}), ...style.font };
                    if (style.fill) cell.fill = style.fill;
                    if (style.border) cell.border = style.border;
                    if (style.alignment) cell.alignment = { ...(cell.alignment || {}), ...style.alignment };
                });
            };

            styleRow(1, {
                font: { bold: true, size: 16 },
                fill: fillGrey('FFD9D9D9'),
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });

            styleRow(2, {
                font: { italic: true },
                fill: fillGrey('FFF5F5F5'),
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });

            styleRow(4, {
                font: { bold: true },
                fill: fillGrey('FFE5E5E5'),
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle', wrapText: true }
            });

            const sortedSubjects = [...selectedSubjects].sort((a, b) => {
                const gradeA = parseInt(a.grade || 0, 10) || 99;
                const gradeB = parseInt(b.grade || 0, 10) || 99;
                if (gradeA !== gradeB) return gradeA - gradeB;
                return String(a.class_name || '').localeCompare(String(b.class_name || '')) || String(a.name || '').localeCompare(String(b.name || ''));
            });

            sortedSubjects.forEach((subject) => {
                const selectedSections = getSelectedSections(subject, selectedSectionsBySubject);
                if (selectedSections.length === 0) return;

                selectedSections.sort((a, b) => String(a.section_name || '').localeCompare(String(b.section_name || '')));

                // 1. Individual Sections
                selectedSections.forEach((section) => {
                    const stats = buildSectionStats(subject, section);
                    const row = worksheet.addRow([
                        subject.grade || '',
                        subject.class_name || '',
                        subject.name || '',
                        section.section_name || '',
                        stats.totalInClass,
                        stats.totalWritten,
                        stats.classAverage !== null ? `${stats.classAverage.toFixed(1)}%` : '',
                        stats.highest,
                        stats.lowest,
                        stats.failedCount,
                        stats.totalWritten > 0 ? `${Math.round((stats.failedCount / stats.totalWritten) * 100)}%` : '0%',
                        stats.passedCount,
                        stats.totalWritten > 0 ? `${Math.round((stats.passedCount / stats.totalWritten) * 100)}%` : '0%',
                        stats.levelDist[1], stats.levelDist[2], stats.levelDist[3],
                        stats.levelDist[4], stats.levelDist[5], stats.levelDist[6], stats.levelDist[7],
                        stats.totalLevels
                    ]);
                    row.alignment = { horizontal: 'center', vertical: 'middle' };
                });

                // 2. Category Totals (e.g. Exam total, Assignment total)
                const assessmentGroups = buildAssessmentGroups(selectedSections);
                assessmentGroups.forEach((group) => {
                    if ((group.key.startsWith('group:') || group.key.startsWith('category:')) && group.sections.length > 0) {
                        const groupStats = buildOverallStats(subject, group.sections);
                        const groupPrefix = group.key.startsWith('group:') ? '[Assessment]' : '[Category]';
                        const groupRow = worksheet.addRow([
                            subject.grade || '',
                            subject.class_name || '',
                            subject.name || '',
                            `${groupPrefix} ${group.label} Total`,
                            subject.student_count || 0,
                            groupStats.totalWritten,
                            groupStats.classAverage !== null ? `${groupStats.classAverage.toFixed(1)}%` : '',
                            groupStats.highest,
                            groupStats.lowest,
                            groupStats.failedCount,
                            groupStats.totalWritten > 0 ? `${Math.round((groupStats.failedCount / groupStats.totalWritten) * 100)}%` : '0%',
                            groupStats.passedCount,
                            groupStats.totalWritten > 0 ? `${Math.round((groupStats.passedCount / groupStats.totalWritten) * 100)}%` : '0%',
                            groupStats.levelDist[1], groupStats.levelDist[2], groupStats.levelDist[3],
                            groupStats.levelDist[4], groupStats.levelDist[5], groupStats.levelDist[6], groupStats.levelDist[7],
                            groupStats.totalLevels
                        ]);
                        groupRow.font = { bold: true };
                        groupRow.fill = fillGrey('FFF5F5F5'); 
                        groupRow.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                });

                // 3. Overall Class Total
                const overallStats = buildOverallStats(subject, selectedSections);
                const totalRow = worksheet.addRow([
                    subject.grade || '',
                    subject.class_name || '',
                    subject.name ? `TOTAL ${subject.name}` : 'TOTAL',
                    'Class overall total',
                    subject.student_count || 0,
                    overallStats.totalWritten,
                    overallStats.classAverage !== null ? `${overallStats.classAverage.toFixed(1)}%` : '',
                    overallStats.highest,
                    overallStats.lowest,
                    overallStats.failedCount,
                    overallStats.totalWritten > 0 ? `${Math.round((overallStats.failedCount / overallStats.totalWritten) * 100)}%` : '0%',
                    overallStats.passedCount,
                    overallStats.totalWritten > 0 ? `${Math.round((overallStats.passedCount / overallStats.totalWritten) * 100)}%` : '0%',
                    overallStats.levelDist[1], overallStats.levelDist[2], overallStats.levelDist[3],
                    overallStats.levelDist[4], overallStats.levelDist[5], overallStats.levelDist[6], overallStats.levelDist[7],
                    overallStats.totalLevels
                ]);

                totalRow.font = { bold: true };
                totalRow.fill = fillGrey('FFE5E5E5');
                totalRow.alignment = { horizontal: 'center', vertical: 'middle' };

                worksheet.addRow([]);

                if (includeMarksSheets) {
                    buildRawMarksSheet(
                        workbook,
                        subject,
                        selectedSections,
                        selectedTerm,
                        username,
                        assessmentWeightsBySubject[String(subject.subject_id)] || {}
                    );
                }
            });

            worksheet.columns = [
                { width: 10 }, { width: 14 }, { width: 22 }, { width: 25 }, { width: 12 },
                { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 },
                { width: 10 }, { width: 12 }, { width: 10 }, { width: 10 }, { width: 10 },
                { width: 10 }, { width: 10 }, { width: 10 }, { width: 10 }, { width: 10 }, { width: 18 }
            ];

            const buffer = await workbook.xlsx.writeBuffer();
            const fileName = `Marksure_Term_${selectedTerm}_Class_Statistics${includeMarksSheets ? '_with_Marks' : ''}.xlsx`;
            saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
        } finally {
            setExporting(false);
        }
    };

    const selectedSubjectCount = selectedSubjectIds.length;
    const selectedSectionCount = subjects.reduce((count, subject) => {
        if (!selectedSubjectIds.includes(String(subject.subject_id))) return count;
        return count + (selectedSectionsBySubject[subject.subject_id] || []).length;
    }, 0);

    return (
        <div className="export-marks-page">
            <div className="export-marks-shell">
                <div className="export-header-card">
                    <div>
                        <p className="export-eyebrow">Marks export</p>
                        <h1>Export class statistics</h1>
                        <p className="export-subtitle">
                            Select a term, choose the classes you want, then decide which recorded assessments to include in one Excel sheet.
                        </p>
                    </div>
                    <div className="export-actions">
                        <Link to="/dashboard" className="export-secondary-btn">Back to Dashboard</Link>
                        <button className="export-primary-btn" onClick={exportWorkbook} disabled={loading || exporting}>
                            <Icons.Download />
                            {exporting ? 'Exporting...' : 'Export Excel'}
                        </button>
                    </div>
                </div>

                <div className="export-toolbar">
                    <label className="toolbar-field">
                        <span>Term</span>
                        <select value={selectedTerm} onChange={(e) => setSelectedTerm(parseInt(e.target.value, 10))}>
                            {TERMS.map((term) => (
                                <option key={term} value={term}>Term {term}</option>
                            ))}
                        </select>
                    </label>

                    <div className="toolbar-summary">
                        <span><Icons.Check /> {selectedSubjectCount} classes selected</span>
                        <span><Icons.Section /> {selectedSectionCount} assessments selected</span>
                    </div>

                    <label className="toolbar-mark-toggle">
                        <input
                            type="checkbox"
                            checked={includeMarksSheets}
                            onChange={(e) => setIncludeMarksSheets(e.target.checked)}
                        />
                        <span>Include marks sheets</span>
                    </label>

                    <button className="toolbar-refresh" onClick={() => loadData(selectedTerm)} disabled={loading || exporting}>
                        <Icons.Refresh />
                        Refresh data
                    </button>
                </div>

                {error && <div className="export-error">{error}</div>}

                {loading ? (
                    <div className="export-loading">Loading classes and assessments...</div>
                ) : sortedGrades.length > 0 ? (
                    sortedGrades.map((grade) => (
                        <section key={`grade-${grade}`} className="grade-block">
                            <div className="grade-block-header">
                                <h2>Grade {grade}</h2>
                                <span>{groupedByGrade[grade].length} class{groupedByGrade[grade].length === 1 ? '' : 'es'}</span>
                            </div>

                            <div className="export-class-grid">
                                {groupedByGrade[grade].map((subject) => {
                                    const isSelected = selectedSubjectIds.includes(String(subject.subject_id));
                                    const sections = subject.sections || [];
                                    const selectedSections = selectedSectionsBySubject[subject.subject_id] || [];
                                    const foundationDefinition = getFoundationDefinitionForSubject(subject);

                                    return (
                                        <div key={subject.subject_id} className={`class-card ${isSelected ? 'selected' : ''}`}>
                                            <div className="class-card-head">
                                                <label className="class-toggle">
                                                    <input
                                                        type="checkbox"
    checked={isSelected}
    onChange={() => toggleSubject(subject)}
                                                    />
                                                    <span>
                                                        <strong>{subject.class_name || 'Unnamed class'}</strong>
                                                        <small>{subject.name}</small>
                                                    </span>
                                                </label>
                                                <button type="button" className="class-select-all" onClick={() => selectAllSections(subject)}>
                                                    Select all
                                                </button>
                                            </div>

                                            <div className="class-stats-line">
                                                <span>Students: {subject.student_count || 0}</span>
                                                <span>Sections: {sections.length}</span>
                                            </div>

                                            <div className="assessment-weights-panel">
                                            <div className="assessment-weights-head">
                                                <strong>{foundationDefinition ? 'CAPS weighting' : 'Convert'}</strong>
                                                <small>{foundationDefinition ? 'Foundation Phase uses the built-in CAPS component weights.' : 'Convert marks to a different scale.'}</small>
                                            </div>
                                                <div className="assessment-weight-list">
                                                    {buildAssessmentGroups(sections).length === 0 ? (
                                                        <p className="empty-sections">No assessment groups found.</p>
                                                    ) : buildAssessmentGroups(sections).map((group) => {
                                                        const subjectWeights = assessmentWeightsBySubject[String(subject.subject_id)] || {};
                                                        const currentWeight = subjectWeights[group.key] ?? (group.rawMax > 0 ? group.rawMax : 100);

                                                        return (
                                                            <label key={group.key} className="assessment-weight-row">
                                                                <span>
                                                                    <strong>{group.label}</strong>
                                                                    <small>{group.sections.length} section{group.sections.length === 1 ? '' : 's'}   raw max {group.rawMax || 0}</small>
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    step="1"
                                                                    value={currentWeight}
                                                                    onChange={(e) => updateAssessmentWeight(subject.subject_id, group.key, e.target.value)}
                                                                    disabled={!isSelected || !!foundationDefinition}
                                                                />
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            <div className="section-list">
                                                {sections.length === 0 ? (
                                                    <p className="empty-sections">No sections recorded for this term.</p>
                                                ) : sections.map((section) => {
                                                    const sectionSelected = selectedSections.map(String).includes(String(section.id));
                                                    const recordedMarks = (subject.marks || []).filter((mark) => String(mark.section_id) === String(section.id)).length;

                                                    return (
                                                        <label key={section.id} className={`section-chip ${sectionSelected ? 'active' : ''}`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={sectionSelected}
                                                                onChange={() => toggleSection(subject.subject_id, section.id)}
                                                                disabled={!isSelected}
                                                            />
                                                            <span>
                                                                <strong>{section.section_name}</strong>
                                                                <small>{recordedMarks} recorded marks</small>
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    ))
                ) : (
                    <div className="export-empty-state">
                        No classes were found for this teacher.
                    </div>
                )}
            </div>
        </div>
    );
}
